import React from "react";
import { ArrowUp, Linkedin, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";

const Footer = () => {
  const { t, currentLanguage } = useLanguage();
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  // New navigation structure matching Header - German labels by default
  const footerSections = {
    valueEngine: {
      title: 'KI-Value-Engine',
      links: [
        { label: 'Die Engine', href: '/ki-value-engine' },
        { label: 'Value-Check', href: '/ki-value-check' },
        { label: 'Value-Dashboard', href: '/value-dashboard' },
        { label: 'KI-Souveränität', href: '/souveraene-cloud' },
      ]
    },
    solutions: {
      title: 'Lösungen',
      links: [
        { label: 'ROI & Business Impact', href: '/roi-business-impact' },
        { label: 'AI Driven Enterprise', href: '/ai-driven-enterprise' },
        { label: 'Transformation', href: '/leistungsangebot/ki-transformation' },
        { label: 'Private Equity', href: '/private-equity' },
      ]
    },
    enablement: {
      title: 'Enablement',
      links: [
        { label: 'Akademie', href: '/leistungsangebot/ki-akademie' },
        { label: 'Workshops', href: '/leistungsangebot/ki-workshops' },
        { label: 'Executive Coaching', href: '/leistungsangebot/ki-coaching' },
      ]
    },
    products: {
      title: 'Produkte',
      links: [
        { label: 'Alle Produkte', href: '/produkte' },
        { label: 'Granalis', href: '/granalis' },
        { label: 'Vox2Doc', href: 'https://vox2doc.de', external: true },
        { label: 'Domicus', href: 'https://domicus.de', external: true },
        { label: 'Fleetdoc', href: 'https://fleetdoc.de', external: true },
        { label: 'Fairspark', href: 'https://fairspark.de', external: true },
        { label: 'Replyly', href: 'https://replyly.online', external: true },
      ]
    },
    expertise: {
      title: 'Expertise',
      links: [
        { label: 'Whitepapers & Studien', href: '/wissenscenter' },
        { label: 'Use Cases', href: '/ai-use-cases' },
        { label: 'Über uns', href: '/ueber-uns' },
      ]
    }
  };

  const legalLinks = [
    { label: t('footer.imprint'), href: "/imprint" },
    { label: t('footer.privacy'), href: "/privacy" },
    { label: t('footer.contact'), href: "/contact" }
  ];

  return (
    <footer className="bg-surface text-ink border-t border-hairline">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-7 gap-8">
          {/* Company Info */}
          <div className="sm:col-span-2">
            <div className="text-2xl font-extrabold tracking-[0.02em]">BROVKO</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-brand-400 mt-1 mb-3">
              The AI Transformation Company
            </div>
            <p className="text-ink-muted text-sm leading-relaxed mb-4">
              {t('footer.company')}
            </p>
            
            <div className="text-xs text-ink-faint">
              <p className="mb-1">{t('footer.companyName')}</p>
              <p className="mb-1">{t('footer.companyDesc1')}</p>
              <p>{t('footer.companyDesc2')}</p>
            </div>

            {/* Contact Info */}
            <div className="mt-4 space-y-1 text-xs text-ink-muted">
              <p>📧 {t('footer.email')}</p>
            </div>

            {/* Social Links */}
            <div className="mt-4 flex items-center gap-3">
              <a
                href="https://www.linkedin.com/company/brovko"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink-muted hover:text-brand-400 transition-colors"
                aria-label="BROVKO auf LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* KI-Value-Engine */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-brand-400">{footerSections.valueEngine.title}</h3>
            <ul className="space-y-2">
              {footerSections.valueEngine.links.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-ink-muted hover:text-ink text-xs transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Lösungen */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-brand-400">{footerSections.solutions.title}</h3>
            <ul className="space-y-2">
              {footerSections.solutions.links.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-ink-muted hover:text-ink text-xs transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Enablement */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-brand-400">{footerSections.enablement.title}</h3>
            <ul className="space-y-2">
              {footerSections.enablement.links.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-ink-muted hover:text-ink text-xs transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Produkte */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-brand-400">{footerSections.products.title}</h3>
            <ul className="space-y-2">
              {footerSections.products.links.map((link, index) => (
                <li key={index}>
                  {link.external ? (
                    <a href={link.href} target="_blank" rel="noopener noreferrer"
                       className="text-ink-muted hover:text-ink text-xs transition-colors inline-flex items-center gap-1">
                      {link.label}
                      <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  ) : (
                    <Link to={link.href} className="text-ink-muted hover:text-ink text-xs transition-colors">
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Expertise */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-brand-400">{footerSections.expertise.title}</h3>
            <ul className="space-y-2">
              {footerSections.expertise.links.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-ink-muted hover:text-ink text-xs transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Partner Program - Extra Link */}
            <div className="mt-4 pt-3 border-t border-hairline">
              <Link to="/partnerprogramm" className="text-ink-muted hover:text-ink text-xs transition-colors">
                Partnerprogramm
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-hairline">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            {/* Copyright */}
            <div className="text-xs text-ink-faint">
              {t('footer.copyright')}
            </div>

            {/* Legal Links */}
            <div className="flex items-center gap-4">
              {legalLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className="text-xs text-ink-faint hover:text-brand-400 transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Back to Top */}
            <button
              onClick={scrollToTop}
              className="bg-brand hover:bg-brand-600 text-ink-onBrand p-1.5 rounded-full transition-all duration-300 transform hover:scale-110"
              aria-label="Nach oben scrollen"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
